import { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Alert,
  Link,
  CircularProgress
} from '@mui/material';
import MergeClient, { IntegrationSyncStatus, IntegrationSyncStatusStatusEnum } from '../../apiClients/MergeClient';
import { useErrorDisplay } from '../ErrorDisplay';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import { useMergeLink } from "@mergeapi/react-merge-link";

// Initialize dayjs plugins
dayjs.extend(relativeTime);

interface MergeLinkIntegrationProps {
  category?: string;
}

const MergeLinkIntegration = ({ category = "hris" }: MergeLinkIntegrationProps) => {
  const [ErrorDisplay, setError] = useErrorDisplay();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setStatus] = useState<IntegrationSyncStatus | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  
  // Only run on mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setLoading(true);
        const apiResponse = await new MergeClient().GetIntegrationSyncStatus();
          setStatus(apiResponse.Content);
          setLoading(false);
        }
      catch (error) {
          setError(error, "Failed to load integration status");
          setLoading(false);
              }
    
    // Cleanup function to prevent state updates after unmount
    // return () => {
    //   isMounted = false;
    // };
}
    
fetchStatus();
  }, []);

  // Handle manual refresh
  const handleRefresh = async () => {
    if (loading || syncing) return;
    
    try {
      setLoading(true);
      const apiResponse = await new MergeClient().GetIntegrationSyncStatus();
      setStatus(apiResponse.Content);
    } catch (error) {
      setError(error, "Failed to refresh integration status");
    } finally {
      setLoading(false);
    }
  };
  
  // Merge link handlers
  const onSuccess = async (publicToken: string) => {
    try {
      await new MergeClient().SwapPublicToken(publicToken);
      // Refresh status
      handleRefresh();
    } catch (error) {
      setError(error, "Failed to complete integration");
    } finally {
      setLoading(false);
    }
  };
  
  const onExit = () => {
    setLoading(false);
  };
  
  const { open } = useMergeLink({
    linkToken,
    onSuccess,
    onExit
  });
  
  // Connect to Merge.dev
  const handleConnect = async () => {
    if (loading) return;
    
    try {
      setLoading(true);
      const linkTokenResponse = await new MergeClient().GetLinkToken(category);
      setLinkToken(linkTokenResponse.Content);
      open();
    } catch (error) {
      setError(error, "Failed to initialize Merge Link");
      setLoading(false);
    }
  };
  
  // Trigger sync
  const handleSync = async () => {
    if (syncing || loading) return;
    
    try {
      setSyncing(true);
      const syncResponse = await new MergeClient().SyncIntegration();
      
      if (!syncResponse.IsError) {
        // Refresh status after successful sync
        try {
          const apiResponse = await new MergeClient().GetIntegrationSyncStatus();
          setStatus(apiResponse.Content);
        } catch (error) {
          setError(error, "Failed to get updated status");
        }
      } else {
        const errorMessage = syncResponse.Errors?.length > 0 
          ? syncResponse.Errors[0] 
          : "Failed to sync integration data";
        setError(new Error(errorMessage), "Failed to sync data");
      }
    } catch (error) {
      setError(error, "Failed to sync data from integration");
    } finally {
      setSyncing(false);
    }
  };
  
  // Render the status display based on current status
  const renderStatusDisplay = () => {
    if (!syncStatus) return null;

    switch (syncStatus.Status) {
      case IntegrationSyncStatusStatusEnum.NotConfigured:
        return (
          <Alert severity="info" icon={<LinkOffIcon />}>
            <Typography variant="body1">
              Not connected to any HRIS system
            </Typography>
            <Typography variant="body2">
              Connect your HRIS system to automate employee data sync
            </Typography>
          </Alert>
        );
      case IntegrationSyncStatusStatusEnum.Syncing:
        return (
          <Alert severity="info" icon={<SyncIcon />}>
            <Typography variant="body1">
              Syncing data from your HRIS system
            </Typography>
            <Typography variant="body2">
              This may take a few minutes depending on the amount of data
            </Typography>
          </Alert>
        );
      case IntegrationSyncStatusStatusEnum.Done:
        return (
          <Alert severity="success" icon={<LinkIcon />}>
            <Typography variant="body1">
              Connected and synced successfully
            </Typography>
            {syncStatus.LastSyncCompleted && (
              <Typography variant="body2">
                Last synced {dayjs(syncStatus.LastSyncCompleted).fromNow()}
              </Typography>
            )}
          </Alert>
        );
      case IntegrationSyncStatusStatusEnum.Failed:
        return (
          <Alert severity="error" icon={<LinkIcon />}>
            <Typography variant="body1">
              Sync failed - please try again
            </Typography>
            {syncStatus.LastSyncCompleted && (
              <Typography variant="body2">
                Last successful sync {dayjs(syncStatus.LastSyncCompleted).fromNow()}
              </Typography>
            )}
          </Alert>
        );
      case IntegrationSyncStatusStatusEnum.NeverSynced:
        return (
          <Alert severity="warning" icon={<LinkIcon />}>
            <Typography variant="body1">
              Connected but never synced
            </Typography>
            <Typography variant="body2">
              Click the Sync button to retrieve data from your HRIS system
            </Typography>
          </Alert>
        );
      default:
        return (
          <Alert severity="info" icon={<LinkOffIcon />}>
            <Typography variant="body1">
              Integration status unknown
            </Typography>
          </Alert>
        );
    }
  };
  
  // Render the action button based on current status
  const renderActionButton = () => {
    if (!syncStatus) return null;

    if (syncStatus.Status === IntegrationSyncStatusStatusEnum.NotConfigured) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={handleConnect}
          startIcon={<LinkIcon />}
          disabled={loading}
        >
          Connect HRIS System
        </Button>
      );
    } else {
      return (
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSync}
            startIcon={<SyncIcon />}
            disabled={syncing || syncStatus.Status === IntegrationSyncStatusStatusEnum.Syncing}
          >
            {syncing || syncStatus.Status === IntegrationSyncStatusStatusEnum.Syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            // onClick={handleDisconnect}
            startIcon={<LinkOffIcon />}
          >
            Disconnect
          </Button>
        </Box>
      );
    }
  };
  
  return (
    <Card elevation={2}>
      <CardContent>
        <ErrorDisplay />
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">
                HRIS Integration
              </Typography>
              <Button 
                startIcon={<RefreshIcon />}
                size="small"
                onClick={handleRefresh}
                disabled={loading || syncing}
              >
                Refresh
              </Button>
            </Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Connect your HR Information System to sync employee data automatically
            </Typography>
          </Grid>
          
          {loading ? (
            <Grid item xs={12} textAlign="center">
              <CircularProgress size={24} />
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                {renderStatusDisplay()}
              </Grid>
              
              <Grid item xs={12}>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  {renderActionButton()}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default MergeLinkIntegration;
